import { createSlice, current, isDraft, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import type { ListEntity, SavedView, ViewFilter } from '../api/types';
import { PREDEFINED_VIEWS } from './predefinedViews';
import { readJson, STORAGE_KEYS } from './storage';

export const PAGE_SIZE = 50;

/** One list's view state: the active view, its unsaved draft, search, paging and custom views. */
export interface ListViewsState {
  activeId: string;
  draft: SavedView;
  q: string;
  limit: number;
  custom: SavedView[];
}

export type ViewsState = Record<ListEntity, ListViewsState>;

/** Deep copy; custom views found inside a reducer are Immer drafts, which cannot be cloned directly. */
const clone = <T extends object>(value: T): T =>
  structuredClone(isDraft(value) ? current<T>(value as Draft<T>) : value);
const ENTITIES: ListEntity[] = ['subscriptions', 'users', 'apps', 'leads'];

export function findView(
  list: Pick<ListViewsState, 'custom'>,
  entity: ListEntity,
  id: string,
): SavedView {
  const predefined = PREDEFINED_VIEWS[entity];
  return (
    list.custom.find((v) => v.id === id) ?? predefined.find((v) => v.id === id) ?? predefined[0]
  );
}

function initialList(entity: ListEntity): ListViewsState {
  const stored = readJson<{ custom: SavedView[]; active?: string }>(STORAGE_KEYS.views(entity), {
    custom: [],
  });
  const custom = Array.isArray(stored.custom) ? stored.custom : [];
  const active = findView({ custom }, entity, stored.active ?? PREDEFINED_VIEWS[entity][0].id);
  return { activeId: active.id, draft: clone(active), q: '', limit: PAGE_SIZE, custom };
}

export const loadViews = (): ViewsState =>
  Object.fromEntries(ENTITIES.map((e) => [e, initialList(e)])) as ViewsState;

const empty = (entity: ListEntity): ListViewsState => {
  const first = PREDEFINED_VIEWS[entity][0];
  return { activeId: first.id, draft: clone(first), q: '', limit: PAGE_SIZE, custom: [] };
};

type EntityPayload<T = object> = PayloadAction<T & { entity: ListEntity }>;

export const viewsSlice = createSlice({
  name: 'views',
  initialState: {
    subscriptions: empty('subscriptions'),
    users: empty('users'),
    apps: empty('apps'),
    leads: empty('leads'),
  },
  reducers: {
    /** Selecting a view resets search, paging and unsaved changes. */
    viewSelected(state, { payload }: EntityPayload<{ id: string }>) {
      const list = state[payload.entity];
      const view = findView(list, payload.entity, payload.id);
      list.activeId = view.id;
      list.draft = clone(view);
      list.q = '';
      list.limit = PAGE_SIZE;
    },
    draftChanged(state, { payload }: EntityPayload<{ draft: SavedView }>) {
      const list = state[payload.entity];
      list.draft = payload.draft;
      list.limit = PAGE_SIZE;
    },
    draftDiscarded(state, { payload }: EntityPayload) {
      const list = state[payload.entity];
      list.draft = clone(findView(list, payload.entity, list.activeId));
      list.limit = PAGE_SIZE;
    },
    /**
     * Drill-down links open "All" (or the `base` view, e.g. Leads › Abandoned checkouts) with extra
     * filters as unsaved changes.
     */
    drilldownApplied(
      state,
      { payload }: EntityPayload<{ filters: ViewFilter[]; base?: string | null }>,
    ) {
      const list = state[payload.entity];
      const all = findView(
        list,
        payload.entity,
        payload.base ?? PREDEFINED_VIEWS[payload.entity][0].id,
      );
      list.activeId = all.id;
      list.draft = { ...clone(all), filters: [...all.filters, ...payload.filters] };
      list.q = '';
      list.limit = PAGE_SIZE;
    },
    searchChanged(state, { payload }: EntityPayload<{ q: string }>) {
      const list = state[payload.entity];
      list.q = payload.q;
      list.limit = PAGE_SIZE;
    },
    moreShown(state, { payload }: EntityPayload) {
      state[payload.entity].limit += PAGE_SIZE;
    },
    customViewCreated(state, { payload }: EntityPayload<{ view: SavedView }>) {
      const list = state[payload.entity];
      list.custom.push(payload.view);
      list.activeId = payload.view.id;
      list.draft = clone(payload.view);
      list.limit = PAGE_SIZE;
    },
    customViewSaved(state, { payload }: EntityPayload) {
      const list = state[payload.entity];
      const index = list.custom.findIndex((v) => v.id === list.activeId);
      if (index >= 0) list.custom[index] = clone(list.draft);
    },
    customViewRenamed(state, { payload }: EntityPayload<{ name: string; description: string }>) {
      const list = state[payload.entity];
      const view = list.custom.find((v) => v.id === list.activeId);
      if (!view) return;
      view.name = payload.name;
      view.description = payload.description;
      list.draft.name = payload.name;
      list.draft.description = payload.description;
    },
    customViewDeleted(state, { payload }: EntityPayload) {
      const list = state[payload.entity];
      list.custom = list.custom.filter((v) => v.id !== list.activeId);
      const first = PREDEFINED_VIEWS[payload.entity][0];
      list.activeId = first.id;
      list.draft = clone(first);
      list.limit = PAGE_SIZE;
    },
  },
  selectors: {
    selectListViews: (state, entity: ListEntity) => state[entity],
  },
});

export const {
  viewSelected,
  draftChanged,
  draftDiscarded,
  drilldownApplied,
  searchChanged,
  moreShown,
  customViewCreated,
  customViewSaved,
  customViewRenamed,
  customViewDeleted,
} = viewsSlice.actions;
export const { selectListViews } = viewsSlice.selectors;
